/**
 * Work: the pure decisions behind the cards.
 *
 * Spec 2026-09-20 (work-cards): Work shows EVERY running briefing. Submissions
 * still to be confirmed come first, as one row per submission with the total
 * price and a Confirm checkmark; confirmed briefs follow as Tracking-Cards with
 * a 3-node rail. `groupRecent` from BriefScreen moved here unchanged in spirit
 * and grew the card projection and the nav badge count.
 *
 * Nothing here fetches or renders. The server decides `briefStatus` (status-sync
 * from Trello); this only maps it to what a customer sees.
 *
 * Confirmed work (spec 2026-10-28, "Client Work view shows the portal
 * lifecycle read-only") is a separate read, `GET /api/work`, mapped by
 * `cardOfWorkItem` below - the one read path for every engine, briefed and
 * briefless alike. `groupWork`'s own `confirmed` list stays as the signal for
 * Home's quick actions (`actionItems`) and the nav badge, which this spec does
 * not touch.
 *
 * @module web/screens/app/work/cards
 */
import type { ClientWorkItem, PortalBrief } from "../../../api/types.ts";

export function isFlatRateBrief(brief: Pick<PortalBrief, "breakdownJson">): boolean {
  try { return JSON.parse(brief.breakdownJson ?? "null")?.billing === "flat_rate"; }
  catch { return false; }
}

export function isFlatRateRow(row: ToConfirmRow): boolean {
  return row.kind === "single" ? isFlatRateBrief(row.brief) : row.briefs.length > 0 && row.briefs.every(isFlatRateBrief);
}

/** One row in the "To confirm" group: a whole submission, or a legacy single brief. */
export type ToConfirmRow =
  | {
      kind: "bundle";
      submissionId: string;
      briefs: PortalBrief[];
      totalFareCents: number;
      amountDueCents: number;
      /** True when every child is priced and the parent is ready or failed-and-retryable. */
      confirmable: boolean;
      /** True when at least one child has no price yet. */
      unpriced: boolean;
      submissionStatus: PortalBrief["submissionStatus"];
      createdAt: number;
    }
  | { kind: "single"; brief: PortalBrief; confirmable: boolean; unpriced: boolean; createdAt: number };

export interface WorkGroups {
  toConfirm: ToConfirmRow[];
  confirmed: PortalBrief[];
}

/** Newest first: the same order the API hands the briefs in. */
export function groupWork(briefs: PortalBrief[]): WorkGroups {
  const toConfirm: ToConfirmRow[] = [];
  const confirmed: PortalBrief[] = [];
  const bundleAt = new Map<string, number>();

  for (const b of briefs) {
    if (b.status === "confirmed") { confirmed.push(b); continue; }
    if (b.status === "refunded") continue;
    if (!b.submissionId) {
      const unpriced = !isFlatRateBrief(b) && b.fareCents === null;
      toConfirm.push({
        kind: "single",
        brief: b,
        confirmable: b.status === "quoted" && (isFlatRateBrief(b) || (b.fareCents ?? 0) > 0),
        unpriced,
        createdAt: b.createdAt,
      });
      continue;
    }
    const at = bundleAt.get(b.submissionId);
    if (at === undefined) {
      bundleAt.set(b.submissionId, toConfirm.length);
      toConfirm.push({
        kind: "bundle",
        submissionId: b.submissionId,
        briefs: [b],
        totalFareCents: 0,
        amountDueCents: 0,
        confirmable: false,
        unpriced: false,
        submissionStatus: b.submissionStatus ?? null,
        createdAt: b.createdAt,
      });
    } else {
      (toConfirm[at] as Extract<ToConfirmRow, { kind: "bundle" }>).briefs.push(b);
    }
  }

  for (const row of toConfirm) {
    if (row.kind !== "bundle") continue;
    // A bundle whose every child is confirmed belongs with the confirmed cards,
    // one card per child. It only reaches here when a child is still quoted.
    row.totalFareCents = row.briefs.reduce((s, b) => s + (b.fareCents ?? 0), 0);
    row.amountDueCents = row.briefs.reduce(
      (sum, b) => sum + (b.status === "quoted" && !b.paymentRef ? (b.fareCents ?? 0) : 0),
      0,
    );
    row.submissionStatus ??= row.briefs[0]?.submissionStatus ?? null;
    row.unpriced = !isFlatRateRow(row) && row.briefs.some(b => b.fareCents === null);
    const priced = row.briefs.length > 0 && row.briefs.every(
      b => (b.status === "quoted" || b.status === "confirmed") && (isFlatRateBrief(b) || (b.fareCents ?? 0) > 0),
    );
    // A terminal bundle failure is an explicit retry, even when a child already
    // has a paymentRef from the failed attempt. The server's parent status owns it.
    row.confirmable = priced && (
      row.submissionStatus === "ready" || row.submissionStatus === "confirmation_failed"
    );
  }

  return { toConfirm, confirmed };
}

/** The number on the Work nav item: submissions waiting for the customer's tap. */
export function pendingCount(briefs: PortalBrief[]): number {
  return groupWork(briefs).toConfirm.filter(r => r.confirmable).length;
}

/** A to-confirm row's title and status line. */
export function toConfirmLabel(row: ToConfirmRow): { title: string; line: string } {
  if (isFlatRateRow(row)) {
    const briefs = row.kind === "bundle" ? row.briefs : [row.brief];
    const title = briefs.length > 1 ? `${briefs.length} briefings` : briefs[0]?.title || "Briefing";
    const status = row.kind === "bundle" ? row.submissionStatus : row.brief.submissionStatus;
    return { title, line: status === "confirmation_failed" ? "Couldn't send to production · try again" : row.confirmable ? "Ready to send" : "Sending to production" };
  }
  if (row.kind === "single") {
    const b = row.brief;
    const title = b.title || "Briefing";
    if (b.needsReviewReason === "no_script") return { title, line: "Add the spoken script" };
    if (b.status === "needs_review" || b.fareCents === null) return { title, line: "Being priced" };
    if (b.paymentRef) return { title, line: "Payment still processing - confirm again" };
    return { title, line: "Ready to confirm" };
  }
  const n = row.briefs.length;
  const title = n > 1 ? `${n} briefings` : (row.briefs[0]?.title || "Briefing");
  switch (row.submissionStatus) {
    case "confirmation_requested":
    case "processing":
      return { title, line: "Confirming automatically" };
    case "confirmation_failed":
      return { title, line: "Confirmation failed - try again" };
    default:
      return { title, line: row.briefs.some(b => b.needsReviewReason === "no_script") ? "Add the spoken script" : row.unpriced ? "Being priced" : "Ready to confirm" };
  }
}

// ============================================================
// TRACKING-CARD (confirmed briefs)
// ============================================================

/** The three nodes of the rail. */
export const NODES = ["Briefed", "In production", "Delivered"] as const;
export type Node = 1 | 2 | 3;

export interface WorkCard {
  /** Which node is lit. */
  node: Node;
  /** The one status line under the title. */
  line: string;
  /** The mono chip, uppercase in CSS. */
  chip: string;
  /** Where a tap goes - a work item always has one, unlike a brief with no card yet. */
  href: string;
  delivered: boolean;
  searching: boolean;
}

/** Map a work item (`GET /api/work`, briefed or briefless) onto the card the customer sees. */
export function cardOfWorkItem(item: ClientWorkItem): WorkCard {
  const who = item.editorFirstName;
  const href = `#work/board/${encodeURIComponent(item.id)}`;
  switch (item.phase) {
    case "delivered":
      return { node: 3, line: "Delivered", chip: "Delivered", href, delivered: true, searching: false };
    case "in_production":
      return { node: 2, line: who ? `In production · ${who}` : "In production", chip: "In production", href, delivered: false, searching: false };
    case "in_revision":
      return { node: 2, line: "Changes requested", chip: "In revision", href, delivered: false, searching: false };
    case "looking_for_editor":
      return { node: 1, line: "Finding your editor…", chip: "Briefed", href, delivered: false, searching: true };
    case "briefing_received":
    default:
      return { node: 1, line: "Briefing received", chip: "Briefed", href, delivered: false, searching: false };
  }
}

/** "Today" / "Yesterday" / "Mon 28 Jul" - the day it was briefed. */
export function dayLabel(ms: number, now = new Date()): string {
  const d = new Date(ms);
  const dayStart = (x: Date) => Date.UTC(x.getFullYear(), x.getMonth(), x.getDate());
  const diff = Math.round((dayStart(now) - dayStart(d)) / 86400000);
  if (diff <= 0) return "Today";
  if (diff === 1) return "Yesterday";
  return d.toLocaleDateString("en-IE", { weekday: "short", day: "numeric", month: "short" });
}

// ============================================================
// HOME: WHAT NEEDS YOU (Abnahme 2026-09-20)
// ============================================================

/**
 * Alan: Home should surface the things that need the customer - an approval,
 * a finished delivery - and nothing about work that is merely in progress.
 * Work keeps the full status; Home keeps the to-do.
 */
export type ActionItem =
  | { kind: "retry"; title: string; href: string }
  | { kind: "approve"; title: string; fareCents: number; href: string; count: number }
  | { kind: "price"; title: string; href: string; noScript?: boolean }
  | { kind: "delivered"; title: string; href: string; editor: string | null };

export function actionItems(briefs: PortalBrief[], max = 6): ActionItem[] {
  const g = groupWork(briefs);
  const out: ActionItem[] = [];
  for (const row of g.toConfirm) {
    const { title } = toConfirmLabel(row);
    if (isFlatRateRow(row)) {
      if (row.confirmable) out.push({ kind: "retry", title, href: "#work" });
      continue;
    }
    if (row.kind === "bundle") {
      if (row.confirmable) out.push({ kind: "approve", title, fareCents: row.totalFareCents, href: "#work", count: row.briefs.length });
      else if (row.unpriced && row.submissionStatus === "ready") out.push({
        kind: "price", title, href: `#start/${encodeURIComponent(row.submissionId)}`,
        ...(row.briefs.some(b => b.needsReviewReason === "no_script") ? { noScript: true } : {}),
      });
    } else if (row.confirmable) {
      out.push({ kind: "approve", title, fareCents: row.brief.fareCents ?? 0, href: "#work", count: 1 });
    }
  }
  for (const b of g.confirmed) {
    if (b.briefStatus !== "delivered") continue;
    const href = b.deliveredUrl && /^https:\/\//i.test(b.deliveredUrl) ? b.deliveredUrl : null;
    if (!href) continue;
    out.push({ kind: "delivered", title: b.title || "Brief", href, editor: b.editorFirstName });
  }
  return out.slice(0, max);
}

// ============================================================
// "PRICE AGAIN" — the sentence after the tap
// ============================================================

/** What POST /api/brief-submissions/:id/reprice answers. */
export interface RepriceResult { repriced: number; stillUnpriced: number }

/** The customer is never left guessing after "Price again" (Anton, 2026-09-21). */
export function repriceMessage(r: RepriceResult): { text: string; tone: "success" | "error" } {
  if (r.stillUnpriced > 0) {
    return {
      tone: "error",
      text: "We still couldn't price this one automatically. Our team has been alerted and will sort it out - nothing is charged.",
    };
  }
  return {
    tone: "success",
    text: r.repriced === 1 ? "Priced - ready to confirm" : `${r.repriced} briefings priced - ready to confirm`,
  };
}
